// Lógica de negocio de cuotas — pura (sin dependencias de Firebase ni DOM).
// La usan tanto el frontend como el cron del Worker.
import type { Alumno, ConfigGeneral, ConfigPractica, Cuota, EstadoCuota } from "./types";

/**
 * Fecha de vencimiento de una cuota: el mes de la cuota + los meses de desfase,
 * en el día de vencimiento configurado para la práctica.
 * Ej: cuota de Marzo (mes=3) con desfase=1 y diaVencimiento=10 -> vence 10 de Abril.
 */
export function calcularVencimiento(
  mes: number,
  ano: number,
  desfase: number,
  diaVencimiento: number,
): string {
  // mes viene 1..12; el índice base 0 + desfase se normaliza con el constructor de Date.
  const d = new Date(ano, mes - 1 + desfase, diaVencimiento);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Días de atraso respecto de hoy (0 si aún no vence). */
export function diasAtraso(fechaVencimiento: string, hoy = new Date()): number {
  const venc = new Date(fechaVencimiento + "T23:59:59");
  const ms = hoy.getTime() - venc.getTime();
  if (ms <= 0) return 0;
  return Math.floor(ms / 86_400_000);
}

/** Recargo por mora sobre el monto (tras descuento), respetando días de gracia. */
export function calcularMora(
  montoNeto: number,
  fechaVencimiento: string,
  cfg: Pick<ConfigGeneral, "interesMoraPct" | "diasGracia">,
  hoy = new Date(),
): number {
  const atraso = diasAtraso(fechaVencimiento, hoy);
  if (atraso <= cfg.diasGracia || cfg.interesMoraPct <= 0) return 0;
  return Math.round((montoNeto * cfg.interesMoraPct) / 100);
}

/** Valor base de la cuota para un alumno en una práctica. */
export function valorBaseCuota(
  alumno: Pick<Alumno, "valorEspecial">,
  practica: ConfigPractica,
): number {
  if (alumno.valorEspecial != null && alumno.valorEspecial > 0) return alumno.valorEspecial;
  return practica.valorCuota;
}

/**
 * Recalcula montos y estado de una cuota según config y la fecha actual.
 * No toca cuotas ya pagadas, exentas o con beca (estados terminales de gestión).
 */
export function recalcularCuota(
  cuota: Cuota,
  cfg: Pick<ConfigGeneral, "interesMoraPct" | "diasGracia">,
  hoy = new Date(),
): Cuota {
  if (cuota.estado === "pagado" || cuota.estado === "exento" || cuota.estado === "beca") {
    return cuota;
  }
  const montoNeto = Math.max(0, cuota.montoBase - cuota.descuento);
  const recargo = calcularMora(montoNeto, cuota.fechaVencimiento, cfg, hoy);
  const atraso = diasAtraso(cuota.fechaVencimiento, hoy);
  let estado: EstadoCuota = cuota.estado === "abono" ? "abono" : cuota.estado;
  if (cuota.estado !== "abono") {
    estado = atraso > 0 ? "pendiente" : "por_vencer";
  }
  return {
    ...cuota,
    recargo,
    montoFinal: montoNeto + recargo,
    estado,
  };
}

/** Descuento por hermanos: se aplica desde el 2º alumno del mismo apoderado. */
export function descuentoHermanos(
  montoBase: number,
  esHermanoAdicional: boolean,
  descuentoPct: number,
): number {
  if (!esHermanoAdicional || descuentoPct <= 0) return 0;
  return Math.round((montoBase * descuentoPct) / 100);
}
